import { useEffect, useRef, useState } from "react";
import { EMPTY_INSPECTION_REPORT, useAddListing } from "../hooks/useAddListing";
import { deleteCar, fetchCars } from "../api/cars";
import { deleteAccessory, fetchAccessories } from "../api/accessories";
import { deleteSpare, fetchSpares } from "../api/spares";
import {
  MAKES,
  BODY_TYPES,
  FUEL_TYPES,
  TRANSMISSIONS,
  CONDITIONS,
  DRIVE_TRAINS,
  LOCATIONS,
  CURRENCIES,
  ACCESSORY_CATEGORIES,
  SPARE_CATEGORIES,
  BADGES,
} from "../data/listingOptions";
import "../styles/addListing.css";
import Navigation from "../components/navigation";

const DRIVER_OPTIONS = ["Self-drive", "Chauffeur-driven", "Either"];
const AVAILABILITY_OPTIONS = [
  { value: "available", label: "Available now" },
  { value: "hired", label: "Currently hired" },
  { value: "maintenance", label: "Under maintenance" },
];
const HIRE_BADGE_OPTIONS = ["", "Popular", "New addition", "Limited stock"];

// ── Small reusable field components ───────────────────────────

function Field({ label, required, error, hint, children }) {
  return (
    <div className={`al-field ${error ? "al-field--error" : ""}`}>
      <label className="al-label">
        {label}
        {required && (
          <span className="al-required" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </label>
      {children}
      {hint && !error && <p className="al-hint">{hint}</p>}
      {error && (
        <p className="al-error-msg" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function Input({ error, ...props }) {
  return (
    <input
      className={`al-input ${error ? "al-input--error" : ""}`}
      {...props}
    />
  );
}

function Select({ error, children, ...props }) {
  return (
    <div className="al-select-wrap">
      <select
        className={`al-select ${error ? "al-input--error" : ""}`}
        {...props}
      >
        {children}
      </select>
      <svg
        className="al-select-chevron"
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M2 3.5L5 6.5L8 3.5"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function Textarea({ error, ...props }) {
  return (
    <textarea
      className={`al-textarea ${error ? "al-input--error" : ""}`}
      rows={4}
      {...props}
    />
  );
}

function SectionTitle({ children }) {
  return <h3 className="al-section-title">{children}</h3>;
}

function Divider() {
  return <div className="al-divider" aria-hidden="true" />;
}

function InspectionReportFields({ form, setField }) {
  const report = form.inspectionReport;
  const update = (key, value) =>
    setField("inspectionReport", { ...report, [key]: value });
  const updatePower = (key, value) =>
    update("powerFeatures", { ...report.powerFeatures, [key]: value });
  if (!report) {
    return (
      <section className="al-section">
        <SectionTitle>Inspection report</SectionTitle>
        <p className="al-hint">
          Optional. Add a summary report to help buyers understand the vehicle's
          condition.
        </p>
        <button
          type="button"
          className="al-listing-edit"
          onClick={() =>
            setField("inspectionReport", {
              ...EMPTY_INSPECTION_REPORT,
              powerFeatures: { ...EMPTY_INSPECTION_REPORT.powerFeatures },
            })
          }
        >
          Add inspection report
        </button>
      </section>
    );
  }
  const fields = [
    ["reportDate", "Report date"],
    ["validUntil", "Valid until"],
    ["plateNumber", "Number plate"],
    ["vinNumber", "VIN number"],
    ["makeModelYear", "Make, model and year"],
    ["engineCc", "Engine cc"],
    ["interiorCondition", "Interior condition"],
    ["exteriorCondition", "Exterior condition"],
    ["accidentHistory", "Accident history"],
    ["wheelWear", "Wheel / tyre wear"],
    ["additionalFeatures", "Additional features"],
    ["infotainmentFeatures", "Infotainment features"],
    ["engineCondition", "Engine condition"],
    ["coolingSystem", "Cooling system"],
    ["electricalSystem", "Electrical system"],
    ["engineSystem", "Engine system"],
    ["startingSystem", "Starting system"],
    ["chargingSystem", "Charging system"],
    ["transmissionSystem", "Transmission system"],
    ["brakeSystem", "Brake system"],
    ["steeringSystem", "Steering system"],
    ["fuelSystem", "Fuel system"],
  ];
  return (
    <section className="al-section">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <SectionTitle>Inspection report</SectionTitle>
        <button
          type="button"
          className="al-listing-delete"
          onClick={() => setField("inspectionReport", null)}
        >
          Remove report
        </button>
      </div>
      <p className="al-hint">
        This report is optional and will appear on the vehicle page when
        published.
      </p>
      <div className="al-grid al-grid--2">
        {fields.map(([key, label]) => (
          <Field key={key} label={label}>
            <Textarea
              rows={
                key.includes("Condition") ||
                key.includes("History") ||
                key.includes("System")
                  ? 2
                  : 1
              }
              value={report[key] || ""}
              onChange={(e) => update(key, e.target.value)}
            />
          </Field>
        ))}
      </div>
      <SectionTitle>Power features</SectionTitle>
      <div className="al-grid al-grid--2">
        {Object.keys(report.powerFeatures).map((key) => (
          <Field key={key} label={key.replace(/([A-Z])/g, " $1")}>
            <Input
              value={report.powerFeatures[key] || ""}
              onChange={(e) => updatePower(key, e.target.value)}
              placeholder="Condition or notes"
            />
          </Field>
        ))}
      </div>
      <Field label="Disclaimer">
        <Textarea
          rows={3}
          value={report.disclaimer || ""}
          onChange={(e) => update("disclaimer", e.target.value)}
        />
      </Field>
    </section>
  );
}

// ── Image uploader ────────────────────────────────────────────

function ImageUploader({ previews, onAdd, onRemove }) {
  const inputRef = useRef(null);

  return (
    <div className="al-images">
      {previews.map((src, i) => (
        <div key={src} className="al-image-thumb">
          <img src={src} alt={`Upload ${i + 1}`} />
          <button
            type="button"
            className="al-image-remove"
            aria-label={`Remove image ${i + 1}`}
            onClick={() => onRemove(i)}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M1.5 1.5l7 7M8.5 1.5l-7 7"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
          {i === 0 && <span className="al-image-primary">Cover</span>}
        </div>
      ))}

      <button
        type="button"
        className="al-image-add"
        aria-label="Add images"
        onClick={() => inputRef.current?.click()}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M12 5v14M5 12h14"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
        <span>Add photos</span>
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="al-image-input"
        aria-label="Upload images"
        onChange={(e) => {
          const picked = Array.from(e.target.files || []);
          e.target.value = "";
          if (picked.length) onAdd(picked);
        }}
      />
    </div>
  );
}

// ── Vehicle fields ────────────────────────────────────────────

function VehicleFields({ form, setField, errors }) {
  return (
    <>
      <SectionTitle>Vehicle details</SectionTitle>
      <div className="al-grid al-grid--2">
        <Field label="Make" required error={errors.make}>
          <Select
            value={form.make}
            onChange={(e) => setField("make", e.target.value)}
            error={errors.make}
          >
            <option value="">Select make</option>
            {MAKES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Model" required error={errors.model}>
          <Input
            type="text"
            placeholder="e.g. Land Cruiser"
            value={form.model}
            onChange={(e) => setField("model", e.target.value)}
            error={errors.model}
          />
        </Field>

        <Field label="Year" required error={errors.year}>
          <Input
            type="number"
            placeholder="e.g. 2022"
            value={form.year}
            onChange={(e) => setField("year", e.target.value)}
            error={errors.year}
            min="1980"
            max={new Date().getFullYear() + 1}
          />
        </Field>

        <Field label="Body type" error={errors.body_type}>
          <Select
            value={form.body_type}
            onChange={(e) => setField("body_type", e.target.value)}
          >
            <option value="">Select body type</option>
            {BODY_TYPES.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Condition" required error={errors.condition}>
          <Select
            value={form.condition}
            onChange={(e) => setField("condition", e.target.value)}
            error={errors.condition}
          >
            <option value="">Select condition</option>
            {CONDITIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Location" required error={errors.location}>
          <Select
            value={form.location}
            onChange={(e) => setField("location", e.target.value)}
            error={errors.location}
          >
            <option value="">Select location</option>
            {LOCATIONS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Divider />
      <SectionTitle>Specifications</SectionTitle>
      <div className="al-grid al-grid--3">
        <Field label="Engine" error={errors.engine}>
          <Input
            type="text"
            placeholder="e.g. 2.0L i4"
            value={form.engine}
            onChange={(e) => setField("engine", e.target.value)}
          />
        </Field>

        <Field label="Transmission" error={errors.transmission}>
          <Select
            value={form.transmission}
            onChange={(e) => setField("transmission", e.target.value)}
          >
            <option value="">Select</option>
            {TRANSMISSIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Fuel type" error={errors.fuel_type}>
          <Select
            value={form.fuel_type}
            onChange={(e) => setField("fuel_type", e.target.value)}
          >
            <option value="">Select</option>
            {FUEL_TYPES.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Drive train" error={errors.drive_train}>
          <Select
            value={form.drive_train}
            onChange={(e) => setField("drive_train", e.target.value)}
          >
            <option value="">Select</option>
            {DRIVE_TRAINS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Colour" error={errors.color}>
          <Input
            type="text"
            placeholder="e.g. Pearl White"
            value={form.color}
            onChange={(e) => setField("color", e.target.value)}
          />
        </Field>

        <Field label="Mileage" error={errors.mileage}>
          <div className="al-input-group">
            <Input
              type="number"
              placeholder="e.g. 45000"
              value={form.mileage}
              onChange={(e) => setField("mileage", e.target.value)}
              min="0"
            />
            <Select
              value={form.mileage_unit}
              onChange={(e) => setField("mileage_unit", e.target.value)}
            >
              <option value="km">km</option>
              <option value="miles">miles</option>
            </Select>
          </div>
        </Field>

        <Field label="Doors" error={errors.doors}>
          <Input
            type="number"
            placeholder="e.g. 4"
            value={form.doors}
            onChange={(e) => setField("doors", e.target.value)}
            min="1"
            max="6"
          />
        </Field>

        <Field label="Seats" error={errors.seats}>
          <Input
            type="number"
            placeholder="e.g. 5"
            value={form.seats}
            onChange={(e) => setField("seats", e.target.value)}
            min="1"
            max="12"
          />
        </Field>
      </div>

      <Divider />
      <SectionTitle>Pricing</SectionTitle>
      <div className="al-grid al-grid--2">
        <Field label="Selling price" required error={errors.price}>
          <div className="al-input-group">
            <Select
              value={form.currency}
              onChange={(e) => setField("currency", e.target.value)}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
            <Input
              type="number"
              placeholder="e.g. 2400000"
              value={form.price}
              onChange={(e) => setField("price", e.target.value)}
              error={errors.price}
              min="0"
            />
          </div>
        </Field>

        <Field label="Badge" hint="Shown as a label on the listing card">
          <Select
            value={form.badge}
            onChange={(e) => setField("badge", e.target.value)}
          >
            {BADGES.vehicle.map((b) => (
              <option key={b} value={b}>
                {b || "None"}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Divider />
      <SectionTitle>Description & features</SectionTitle>
      <Field label="Description" error={errors.description}>
        <Textarea
          placeholder="Describe the vehicle's history, condition, and any notable features..."
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
        />
      </Field>

      <Field
        label="Features"
        hint="Enter as comma-separated values: Leather seats, Sunroof, Cruise control"
        error={errors.features}
      >
        <Textarea
          rows={2}
          placeholder="Leather seats, Sunroof, Reverse camera, Apple CarPlay..."
          value={form.features}
          onChange={(e) => setField("features", e.target.value)}
        />
      </Field>
    </>
  );
}

function HireFields({ form, setField, errors }) {
  return (
    <>
      <SectionTitle>Hire car details</SectionTitle>
      <div className="al-grid al-grid--2">
        <Field label="Make" required error={errors.make}>
          <Select
            value={form.make}
            onChange={(e) => setField("make", e.target.value)}
            error={errors.make}
          >
            <option value="">Select make</option>
            {MAKES.map((make) => (
              <option key={make} value={make}>
                {make}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Model" required error={errors.model}>
          <Input
            type="text"
            placeholder="e.g. Land Cruiser"
            value={form.model}
            onChange={(e) => setField("model", e.target.value)}
            error={errors.model}
          />
        </Field>
        <Field label="Year" required error={errors.year}>
          <Input
            type="number"
            placeholder="e.g. 2022"
            value={form.year}
            onChange={(e) => setField("year", e.target.value)}
            error={errors.year}
            min="1980"
            max={new Date().getFullYear() + 1}
          />
        </Field>
        <Field label="Pickup location" required error={errors.location}>
          <Select
            value={form.location}
            onChange={(e) => setField("location", e.target.value)}
            error={errors.location}
          >
            <option value="">Select location</option>
            {LOCATIONS.map((location) => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Transmission" error={errors.transmission}>
          <Select
            value={form.transmission}
            onChange={(e) => setField("transmission", e.target.value)}
          >
            <option value="">Select</option>
            {TRANSMISSIONS.map((transmission) => (
              <option key={transmission} value={transmission}>
                {transmission}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Fuel type" error={errors.fuel_type}>
          <Select
            value={form.fuel_type}
            onChange={(e) => setField("fuel_type", e.target.value)}
          >
            <option value="">Select</option>
            {FUEL_TYPES.map((fuelType) => (
              <option key={fuelType} value={fuelType}>
                {fuelType}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Fuel consumption (L/100 km)" error={errors.fuel_consumption}>
          <Input
            type="number"
            placeholder="e.g. 7.5"
            value={form.fuel_consumption}
            onChange={(e) => setField("fuel_consumption", e.target.value)}
            min="0"
            step="0.1"
          />
        </Field>
        <Field label="Seats" error={errors.seats}>
          <Input
            type="number"
            placeholder="e.g. 5"
            value={form.seats}
            onChange={(e) => setField("seats", e.target.value)}
            min="1"
            max="12"
          />
        </Field>
        <Field label="Driver option" required error={errors.driver_option}>
          <Select
            value={form.driver_option}
            onChange={(e) => setField("driver_option", e.target.value)}
            error={errors.driver_option}
          >
            <option value="">Select</option>
            {DRIVER_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Divider />
      <SectionTitle>Hire terms</SectionTitle>
      <div className="al-grid al-grid--2">
        <Field label="Daily rate" required error={errors.price}>
          <div className="al-input-group">
            <Select
              value={form.currency}
              onChange={(e) => setField("currency", e.target.value)}
            >
              {CURRENCIES.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </Select>
            <Input
              type="number"
              placeholder="e.g. 8000"
              value={form.price}
              onChange={(e) => setField("price", e.target.value)}
              error={errors.price}
              min="0"
            />
          </div>
        </Field>
        <Field label="Minimum hire period (days)" error={errors.min_hire_days}>
          <Input
            type="number"
            placeholder="e.g. 2"
            value={form.min_hire_days}
            onChange={(e) => setField("min_hire_days", e.target.value)}
            min="1"
          />
        </Field>
        <Field label="Mileage limit per day (km)" error={errors.mileage_limit}>
          <Input
            type="number"
            placeholder="e.g. 250"
            value={form.mileage_limit}
            onChange={(e) => setField("mileage_limit", e.target.value)}
            min="0"
          />
        </Field>
        <Field label="Security deposit" error={errors.deposit}>
          <Input
            type="number"
            placeholder="e.g. 20000"
            value={form.deposit}
            onChange={(e) => setField("deposit", e.target.value)}
            min="0"
          />
        </Field>
        <Field label="Availability" error={errors.availability}>
          <Select
            value={form.availability}
            onChange={(e) => setField("availability", e.target.value)}
          >
            {AVAILABILITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Badge" hint="Shown as a label on the listing card">
          <Select
            value={form.badge}
            onChange={(e) => setField("badge", e.target.value)}
          >
            {HIRE_BADGE_OPTIONS.map((badge) => (
              <option key={badge} value={badge}>
                {badge || "None"}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Divider />
      <SectionTitle>Description & features</SectionTitle>
      <Field label="Description" error={errors.description}>
        <Textarea
          placeholder="Describe the car, hire conditions, and what's included..."
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
        />
      </Field>
      <Field
        label="Features"
        hint="Enter as comma-separated values: Air conditioning, Bluetooth, Child seat available"
        error={errors.features}
      >
        <Textarea
          rows={2}
          placeholder="Air conditioning, Bluetooth, Child seat available..."
          value={form.features}
          onChange={(e) => setField("features", e.target.value)}
        />
      </Field>
    </>
  );
}

// ── Accessory fields ──────────────────────────────────────────

function AccessoryFields({
  form,
  setField,
  errors,
  specs,
  addSpecRow,
  removeSpecRow,
  updateSpecRow,
}) {
  return (
    <>
      <SectionTitle>Accessory details</SectionTitle>
      <div className="al-grid al-grid--2">
        <Field label="Category" required error={errors.category}>
          <Select
            value={form.category}
            onChange={(e) => setField("category", e.target.value)}
            error={errors.category}
          >
            <option value="">Select category</option>
            {ACCESSORY_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Brand" required error={errors.brand}>
          <Input
            type="text"
            placeholder="e.g. Pioneer"
            value={form.brand}
            onChange={(e) => setField("brand", e.target.value)}
            error={errors.brand}
          />
        </Field>

        <Field label="Product name" required error={errors.name}>
          <Input
            type="text"
            placeholder="e.g. Pioneer DMH-WT8600 Head Unit"
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            error={errors.name}
          />
        </Field>

        <Field
          label="SKU"
          hint="Stock keeping unit / product code"
          error={errors.sku}
        >
          <Input
            type="text"
            placeholder="e.g. PI-HU-WT8600"
            value={form.sku}
            onChange={(e) => setField("sku", e.target.value)}
          />
        </Field>
      </div>

      <Divider />
      <SectionTitle>Pricing & stock</SectionTitle>
      <div className="al-grid al-grid--2">
        <Field label="Price" required error={errors.price}>
          <div className="al-input-group">
            <Select
              value={form.currency}
              onChange={(e) => setField("currency", e.target.value)}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
            <Input
              type="number"
              placeholder="e.g. 28000"
              value={form.price}
              onChange={(e) => setField("price", e.target.value)}
              error={errors.price}
              min="0"
            />
          </div>
        </Field>

        <Field label="Stock quantity" required error={errors.stock}>
          <Input
            type="number"
            placeholder="e.g. 12"
            value={form.stock}
            onChange={(e) => setField("stock", e.target.value)}
            error={errors.stock}
            min="0"
          />
        </Field>

        <Field label="Badge" hint="Shown as a label on the listing card">
          <Select
            value={form.badge}
            onChange={(e) => setField("badge", e.target.value)}
          >
            {BADGES.accessory.map((b) => (
              <option key={b} value={b}>
                {b || "None"}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Divider />
      <SectionTitle>Compatibility & description</SectionTitle>
      <Field
        label="Compatible with"
        hint="Enter as comma-separated values: H7 socket, Universal fit"
        error={errors.compatibility}
      >
        <Input
          type="text"
          placeholder="Universal fit, H7, DIN 2..."
          value={form.compatibility}
          onChange={(e) => setField("compatibility", e.target.value)}
        />
      </Field>

      <Field label="Description" error={errors.description}>
        <Textarea
          placeholder="Describe the product, its benefits, and what's in the box..."
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
        />
      </Field>

      <Divider />
      <SectionTitle>Specs</SectionTitle>
      <p className="al-hint" style={{ marginBottom: 12 }}>
        Add key technical specs as label / value pairs.
      </p>
      <div className="al-specs-list">
        {specs.map((row, i) => (
          <div key={i} className="al-spec-row">
            <Input
              type="text"
              placeholder="Label e.g. Wattage"
              value={row.key}
              onChange={(e) => updateSpecRow(i, "key", e.target.value)}
            />
            <Input
              type="text"
              placeholder="Value e.g. 55W per pair"
              value={row.value}
              onChange={(e) => updateSpecRow(i, "value", e.target.value)}
            />
            {specs.length > 1 && (
              <button
                type="button"
                className="al-spec-remove"
                aria-label="Remove spec row"
                onClick={() => removeSpecRow(i)}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M1 1l10 10M11 1L1 11"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>
      <button type="button" className="al-add-spec-btn" onClick={addSpecRow}>
        <svg
          width="13"
          height="13"
          viewBox="0 0 13 13"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6.5 1v11M1 6.5h11"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
        Add spec row
      </button>
    </>
  );
}

// ── Spare parts fields ────────────────────────────────────────

function SpareFields({ form, setField, errors }) {
  return (
    <>
      <SectionTitle>Spare part details</SectionTitle>
      <div className="al-grid al-grid--2">
        <Field label="Category" required error={errors.category}>
          <Select
            value={form.category}
            onChange={(e) => setField("category", e.target.value)}
            error={errors.category}
          >
            <option value="">Select category</option>
            {SPARE_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Part name" required error={errors.name}>
          <Input
            type="text"
            placeholder="e.g. Engine Oil Filter"
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            error={errors.name}
          />
        </Field>

        <Field
          label="SKU/Part number"
          hint="Stock keeping unit or OEM part number"
          error={errors.sku}
        >
          <Input
            type="text"
            placeholder="e.g. OEM-1234567"
            value={form.sku}
            onChange={(e) => setField("sku", e.target.value)}
          />
        </Field>

        <Field label="Manufacturer" error={errors.manufacturer}>
          <Input
            type="text"
            placeholder="e.g. Toyota"
            value={form.manufacturer}
            onChange={(e) => setField("manufacturer", e.target.value)}
          />
        </Field>

        <Field label="Supplier" error={errors.supplier}>
          <Input
            type="text"
            placeholder="e.g. Auto Parts Nairobi"
            value={form.supplier}
            onChange={(e) => setField("supplier", e.target.value)}
          />
        </Field>
      </div>

      <Divider />
      <SectionTitle>Pricing & inventory</SectionTitle>
      <div className="al-grid al-grid--2">
        <Field label="Price" required error={errors.price}>
          <div className="al-input-group">
            <Select
              value={form.currency}
              onChange={(e) => setField("currency", e.target.value)}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
            <Input
              type="number"
              placeholder="e.g. 3500"
              value={form.price}
              onChange={(e) => setField("price", e.target.value)}
              error={errors.price}
              min="0"
            />
          </div>
        </Field>

        <Field label="Quantity in stock" required error={errors.quantity}>
          <Input
            type="number"
            placeholder="e.g. 50"
            value={form.quantity}
            onChange={(e) => setField("quantity", e.target.value)}
            error={errors.quantity}
            min="0"
          />
        </Field>

        <Field label="Badge" hint="Shown as a label on the listing card">
          <Select
            value={form.badge}
            onChange={(e) => setField("badge", e.target.value)}
          >
            {BADGES.spare.map((b) => (
              <option key={b} value={b}>
                {b || "None"}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Divider />
      <SectionTitle>Description</SectionTitle>
      <Field label="Description" error={errors.description}>
        <Textarea
          placeholder="Describe the spare part, condition, compatibility, and any other details..."
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
        />
      </Field>
    </>
  );
}

function listingTitle(listingType, listing) {
  if (listingType === "spare") return listing.name;
  if (listingType === "accessory") return listing.title;
  return `${listing.make} ${listing.model}`;
}

function ListingManager({ listingType, refreshToken, onEdit }) {
  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setError("");

    const loadListings = async () => {
      try {
        const result =
          listingType === "spare"
            ? await fetchSpares({ sellerOnly: "true" })
            : listingType === "accessory"
              ? await fetchAccessories({ sellerOnly: "true" })
              : await fetchCars(
                  {
                    type: listingType === "hire" ? "hire" : "vehicle",
                    sellerOnly: "true",
                  },
                  1,
                  100,
                );

        if (!active) return;
        setListings(Array.isArray(result) ? result : result.cars || []);
      } catch (loadError) {
        if (active) setError(loadError.message || "Unable to load listings.");
      } finally {
        if (active) setIsLoading(false);
      }
    };

    loadListings();
    return () => {
      active = false;
    };
  }, [listingType, refreshToken]);

  const handleDelete = async (listing) => {
    const title = listingTitle(listingType, listing);
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;

    try {
      if (listingType === "spare") await deleteSpare(listing.id);
      else if (listingType === "accessory") await deleteAccessory(listing.id);
      else await deleteCar(listing.id);
      setListings((current) =>
        current.filter((item) => item.id !== listing.id),
      );
    } catch (deleteError) {
      setError(deleteError.message || "Unable to delete this listing.");
    }
  };

  return (
    <section className="al-manager" aria-labelledby="manage-listings-heading">
      <div className="al-manager-header">
        <div>
          <h2 id="manage-listings-heading" className="al-manager-title">
            Existing listings
          </h2>
          <p className="al-subheading">
            Edit details, add photos, or remove an item from the catalogue.
          </p>
        </div>
        <span className="al-manager-count">
          {listings.length} {listings.length === 1 ? "listing" : "listings"}
        </span>
      </div>

      {error && (
        <p className="al-submit-error" role="alert">
          {error}
        </p>
      )}
      {isLoading ? (
        <p className="al-manager-empty">Loading listings...</p>
      ) : listings.length === 0 ? (
        <p className="al-manager-empty">
          No{" "}
          {listingType === "spare" ? "spare parts" : `${listingType} listings`}{" "}
          found.
        </p>
      ) : (
        <div className="al-listing-list">
          {listings.map((listing) => (
            <div className="al-listing-row" key={listing.id}>
              <div className="al-listing-image">
                {listing.images?.[0] ? (
                  <img src={listing.images[0]} alt="" />
                ) : (
                  <span aria-hidden="true">No photo</span>
                )}
              </div>
              <div className="al-listing-info">
                <strong>{listingTitle(listingType, listing)}</strong>
                <span>
                  {listing.currency || "KES"}{" "}
                  {Number(listing.price || 0).toLocaleString()}
                  {listing.description
                    ? " · Description added"
                    : " · No description"}
                </span>
              </div>
              <div className="al-listing-actions">
                <button
                  type="button"
                  className="al-listing-edit"
                  onClick={() => onEdit(listing)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="al-listing-delete"
                  onClick={() => handleDelete(listing)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function AddListing({ onSubmit }) {
  const [refreshToken, setRefreshToken] = useState(0);
  const {
    listingType,
    switchType,
    editingId,
    startEditing,
    form,
    setField,
    errors,
    imagePreviews,
    handleImages,
    removeImage,
    addSpecRow,
    removeSpecRow,
    updateSpecRow,
    accessorySpecs,
    isSubmitting,
    submitSuccess,
    handleSubmit,
    resetForm,
  } = useAddListing({
    onSubmit: (savedListing) => {
      setRefreshToken((value) => value + 1);
      if (onSubmit) onSubmit(savedListing);
    },
  });

  let userName = "there";
  const currentHour = new Date().getHours();
  const timeGreeting =
    currentHour < 12
      ? "Good Morning"
      : currentHour < 18
        ? "Good afternoon"
        : "Good Evening";
  try {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    userName =
      storedUser.yardName ||
      storedUser.shopName ||
      storedUser.businessName ||
      storedUser.companyName ||
      storedUser.yard ||
      storedUser.shop ||
      userName;
  } catch {
    // Keep the fallback when stored user data is unavailable or invalid.
  }

  return (
    <>
      <Navigation />
      <div className="al-wrap">
        <h1 className="welcome-msg">{timeGreeting}, Welcome back!</h1>
        <div className="al-header">
          <div>
            <h1 className="al-heading">
              {editingId ? "Edit listing" : "Add listing"}
            </h1>
            <p className="al-subheading">
              {editingId
                ? "Update the details, description, or photos for this listing."
                : listingType === "vehicle"
                  ? "Fill in the vehicle details below to publish a new listing."
                  : listingType === "accessory"
                    ? "Fill in the accessory details below to add it to the shop."
                    : listingType === "hire"
                      ? "Fill in the details below to add a car to your hire fleet."
                      : "Fill in the spare part details below to add it to the inventory."}
            </p>
          </div>

          {/* Type toggle */}
          <div
            className="al-type-toggle"
            role="group"
            aria-label="Listing type"
          >
            <button
              type="button"
              className={`al-type-btn ${listingType === "vehicle" ? "al-type-btn--active" : ""}`}
              onClick={() => switchType("vehicle")}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.27097 8.214C6.02074 6.312 6.92262 5 8.96427 5H14.4369C16.4766 5 17.3795 6.312 18.1302 8.214L18.88 10.249C19.4387 10.2559 19.9519 10.5661 20.2284 11.064C20.3911 11.3476 20.4763 11.6709 20.4751 12V14.624C20.4824 15.2367 20.1808 15.8098 19.6776 16.14C19.4382 16.2929 19.162 16.3739 18.88 16.374H4.52022C4.23827 16.3739 3.96199 16.2929 3.72267 16.14C3.21939 15.8098 2.91786 15.2368 2.92512 14.624V12C2.92406 11.6713 3.00927 11.3483 3.17179 11.065C3.44833 10.5671 3.96155 10.2569 4.52022 10.25L5.27097 8.214Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.86629 16.375C6.86629 15.9608 6.53051 15.625 6.11629 15.625C5.70208 15.625 5.36629 15.9608 5.36629 16.375H6.86629ZM6.11629 17.688H5.36629C5.36629 17.6958 5.36641 17.7036 5.36666 17.7114L6.11629 17.688ZM4.91899 19L4.89219 19.7495C4.90986 19.7502 4.92754 19.7502 4.9452 19.7496L4.91899 19ZM3.72267 17.687L4.47228 17.711C4.47254 17.703 4.47267 17.695 4.47267 17.687L3.72267 17.687ZM4.47267 16.14C4.47267 15.7258 4.13688 15.39 3.72267 15.39C3.30845 15.39 2.97267 15.7258 2.97267 16.14H4.47267ZM4.52022 9.49902C4.106 9.49902 3.77022 9.83481 3.77022 10.249C3.77022 10.6632 4.106 10.999 4.52022 10.999V9.49902ZM18.88 10.999C19.2942 10.999 19.63 10.6632 19.63 10.249C19.63 9.83481 19.2942 9.49902 18.88 9.49902V10.999ZM18.0349 16.374C18.0349 15.9598 17.6991 15.624 17.2849 15.624C16.8707 15.624 16.5349 15.9598 16.5349 16.374H18.0349ZM17.2849 17.687L18.0335 17.7336C18.0344 17.7181 18.0349 17.7026 18.0349 17.687H17.2849ZM17.8602 18.8173L18.2561 18.1803L18.2561 18.1803L17.8602 18.8173ZM19.1033 18.8173L18.7074 18.1803L18.7074 18.1803L19.1033 18.8173ZM19.6785 17.687H18.9285C18.9285 17.7026 18.929 17.7181 18.93 17.7336L19.6785 17.687ZM20.4285 16.14C20.4285 15.7258 20.0928 15.39 19.6785 15.39C19.2643 15.39 18.9285 15.7258 18.9285 16.14H20.4285ZM5.85012 12.75C5.4359 12.75 5.10012 13.0858 5.10012 13.5C5.10012 13.9142 5.4359 14.25 5.85012 14.25V12.75ZM6.82512 14.25C7.23933 14.25 7.57512 13.9142 7.57512 13.5C7.57512 13.0858 7.23933 12.75 6.82512 12.75V14.25ZM16.5751 12.75C16.1609 12.75 15.8251 13.0858 15.8251 13.5C15.8251 13.9142 16.1609 14.25 16.5751 14.25V12.75ZM17.5501 14.25C17.9643 14.25 18.3001 13.9142 18.3001 13.5C18.3001 13.0858 17.9643 12.75 17.5501 12.75V14.25ZM5.36629 16.375V17.688H6.86629V16.375H5.36629ZM5.36666 17.7114C5.37622 18.0175 5.14582 18.2416 4.89278 18.2505L4.9452 19.7496C6.05927 19.7106 6.90017 18.7608 6.86593 17.6646L5.36666 17.7114ZM4.94579 18.2505C4.69273 18.2415 4.46248 18.0171 4.47228 17.711L2.97305 17.663C2.93793 18.7592 3.77813 19.7097 4.89219 19.7495L4.94579 18.2505ZM4.47267 17.687V16.14H2.97267V17.687H4.47267ZM4.52022 10.999H18.88V9.49902H4.52022V10.999ZM16.5349 16.374V17.687H18.0349V16.374H16.5349ZM16.5364 17.6404C16.4912 18.3667 16.8425 19.0678 17.4642 19.4543L18.2561 18.1803C18.1138 18.0919 18.0217 17.9219 18.0335 17.7336L16.5364 17.6404ZM17.4642 19.4543C18.0887 19.8424 18.8747 19.8424 19.4992 19.4543L18.7074 18.1803C18.5678 18.2671 18.3957 18.2671 18.2561 18.1803L17.4642 19.4543ZM19.4992 19.4543C20.121 19.0678 20.4723 18.3667 20.4271 17.6404L18.93 17.7336C18.9417 17.9219 18.8497 18.0919 18.7074 18.1803L19.4992 19.4543ZM20.4285 17.687V16.14H18.9285V17.687H20.4285ZM5.85012 14.25H6.82512V12.75H5.85012V14.25ZM16.5751 14.25H17.5501V12.75H16.5751V14.25Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Vehicle
            </button>
            <button
              type="button"
              className={`al-type-btn ${listingType === "accessory" ? "al-type-btn--active" : ""}`}
              onClick={() => switchType("accessory")}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="2"
                  y="7"
                  width="20"
                  height="14"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <path
                  d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
              Accessory
            </button>
            <button
              type="button"
              className={`al-type-btn ${listingType === "spare" ? "al-type-btn--active" : ""}`}
              onClick={() => switchType("spare")}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M12 2L15.09 8.26H22L17.45 12.5L19.54 18.76L12 14.5L4.46 18.76L6.55 12.5L2 8.26H8.91L12 2Z"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Spare Parts
            </button>
            <button
              type="button"
              className={`al-type-btn ${listingType === "hire" ? "al-type-btn--active" : ""}`}
              onClick={() => switchType("hire")}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="7.5"
                  cy="15.5"
                  r="3.5"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <path
                  d="M10 13L19 4M19 4h-4M19 4v4"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Hire
            </button>
          </div>
        </div>

        <form className="al-form" onSubmit={handleSubmit} noValidate>
          {/* ── Images ── */}
          <section className="al-section">
            <SectionTitle>Photos</SectionTitle>
            <p className="al-hint" style={{ marginBottom: 12 }}>
              The first photo will be used as the cover image. Add up to 15.
            </p>
            <ImageUploader
              key={listingType}
              previews={imagePreviews}
              onAdd={handleImages}
              onRemove={removeImage}
            />
          </section>

          <Divider />

          {/* ── Conditional fields ── */}
          <section className="al-section">
            {listingType === "vehicle" ? (
              <>
                <VehicleFields
                  form={form}
                  setField={setField}
                  errors={errors}
                />
                <Divider />
                <InspectionReportFields form={form} setField={setField} />
              </>
            ) : listingType === "hire" ? (
              <HireFields form={form} setField={setField} errors={errors} />
            ) : listingType === "accessory" ? (
              <AccessoryFields
                form={form}
                setField={setField}
                errors={errors}
                specs={accessorySpecs}
                addSpecRow={addSpecRow}
                removeSpecRow={removeSpecRow}
                updateSpecRow={updateSpecRow}
              />
            ) : (
              <SpareFields form={form} setField={setField} errors={errors} />
            )}
          </section>

          {/* ── Submit error ── */}
          {errors._submit && (
            <p className="al-submit-error" role="alert">
              {errors._submit}
            </p>
          )}

          {/* ── Actions ── */}
          <div className="al-form-actions">
            <button
              type="button"
              className="al-btn-reset"
              onClick={resetForm}
              disabled={isSubmitting}
            >
              Clear form
            </button>
            <button
              type="submit"
              className={`al-btn-submit ${submitSuccess ? "al-btn-submit--success" : ""}`}
              disabled={isSubmitting || submitSuccess}
            >
              {submitSuccess ? (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Listing saved
                </>
              ) : isSubmitting ? (
                "Saving..."
              ) : editingId ? (
                "Save changes"
              ) : (
                `Publish ${listingType === "spare" ? "spare part" : listingType === "hire" ? "hire car" : listingType}`
              )}
            </button>
          </div>
        </form>

        <ListingManager
          listingType={listingType}
          refreshToken={refreshToken}
          onEdit={(listing) => startEditing(listingType, listing)}
        />
      </div>
    </>
  );
}
